import { Factory } from '../../services/Factory';
import { IProject } from '../../model/project/IProject';
import { Collection } from '../../model/collections/Collection';
import { CollectionService } from '../../services/collections/CollectionService';
import { ProcessingService } from '../../services/processing/ProcessingService';
import { DropboxOAuthService } from '../../services/oauth/DropboxOAuthService';
import { Status } from '../../model/project/Status';
import { CustodianService } from '../../services/custodian/CustodianService';
import { SFTPService, ForensicImageType } from '../../services/storage/SFTPService';
import { StorageService } from '../../services/storage/StorageService';
import { PIIDetectionMode } from '../../model/collections/Collection';
import { AccountService } from '../../services/account/AccountService';
import { INotificationService } from '../../services/notification';
import { IRootScope } from '../../IRootScope';

export interface IAddCollectionControllerScope extends any {
  SourceType: any,
  project: any,
  isOrganizationProject: boolean,
  newCollection: any,
  source: any,
  saving: boolean,
  uploading: boolean,
  verifying: boolean,
  step: number,
  maxSteps: number,
  close: Function;
  acceptTermsAndConditions: boolean;
}

export enum SourceType {
  Upload = "upload",
  IIO = "iio",
  IIO_Qumulo = "iio_qumulo",
  FTP = "ftp",
  SFTP = "sftp",
  S3 = "s3",
  S3_Qumulo = "s3_qumulo",
  IMAP = "imap",
  DropBox = "dropbox",
  ForensicImage = "forensicimage"
}

export enum ForensicImageValidationStatus {
  VALIDATING = "Validating",
  VALID = "Valid",
  INVALID = "Invalid"
}

export class AddCollectionController {
  constructor(rootScope: IRootScope, scope: IAddCollectionControllerScope, $timeout: angular.ITimeoutService, $location: angular.ILocationService, $uibModalInstance, $uibModal, project: any, collectionService: CollectionService, processingService: ProcessingService, notificationService: INotificationService, sftpService: SFTPService, storageService: StorageService, dropboxOAuthService: DropboxOAuthService, custodianService: CustodianService, accountService: AccountService) {
    let savedFilesState: any;
    let savedUploadSource: { files: any; valid: string } = { files: { active: false, file: null }, valid: "" };
    const sources = {
      upload: {
        type: SourceType.Upload,
        files: {
          active: false,
          file: null
        }
      },
      iio: {
        type: SourceType.IIO,
        path: null,
        baseDir: "",
        files: {
          type: "",
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          sftpService.GetUploadsListing(scope.project, scope.source.baseDir, null).then(response => {
            scope.source.files.type = response.data.type;
            scope.source.files.active = false;
            scope.source.files.list = response.data.files;
          }, () => {
            scope.source.files = savedFilesState;
            notificationService.addError({
              title: 'Error fetching from SFTP',
              description: "That's embarrassing, please try again later",
              timeout: 5000
            });
          });
        }
      },
      iio_qumulo: {
        type: SourceType.IIO_Qumulo,
        path: null,
        baseDir: "",
        files: {
          type: "",
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          storageService.GetIioQumuloListing(scope.project, scope.source.baseDir, null).then(response => {
            scope.source.files.active = false;
            scope.source.files.list = response.data.foldersFiles;
          }, () => {
            scope.source.files = savedFilesState;
            notificationService.addError({
              title: 'Error fetching from Client Storage',
              description: "That's embarrassing, please try again later",
              timeout: 5000
            });
          });
        }
      },
      ftp: {
        type: SourceType.FTP,
        hostname: "",
        port: 21,
        login: "",
        password: "",
        baseDir: "",
        path: "",
        secure: false,
        files: {
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          if (scope.source.ftp.secure) {
            storageService.GetSftpListing(scope.source).success(response => {
              scope.source.files.active = false;
              scope.source.files.list = response.files;
            }).error(() => scope.source.files.active = false);
          } else {
            storageService.GetFtpListing(scope.source).success(response => {
              scope.source.files.active = false;
              scope.source.files.list = response.files;
            }).error(() => scope.source.files.active = false);
          }
        }
      },
      s3: {
        type: SourceType.S3,
        bucketName: "",
        accessKey: "",
        secretKey: "",
        baseDir: "",
        path: "",
        files: {
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          storageService.GetS3Listing(scope.source).success(response => {
            scope.source.files.active = false;
            scope.source.files.list = response.files;
          }).error(() => scope.source.files.active = false);
        }
      },
      s3_qumulo: {
        type: SourceType.S3_Qumulo,
        endpoint: "",
        bucketName: "",
        accessKey: "",
        secretKey: "",
        baseDir: "",
        path: "",
        files: {
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          storageService.GetS3QumuloListing(scope.source).success(response => {
            scope.source.files.active = false;
            scope.source.files.list = response.files;
          }).error(() => scope.source.files.active = false);
        }
      },
      imap: {
        type: SourceType.IMAP,
        host: "",
        port: 993,
        username: "",
        password: "",
        provider: "",
        ssl: true
      },
      dropbox: {
        type: SourceType.DropBox,
        user: "",
        baseDir: "",
        accessToken: "",
        path: "",
        files: {
          active: false,
          selectedIndex: -1,
          list: []
        }
      },
      forensicImage: {
        type: SourceType.ForensicImage,
        forensicImageType: ForensicImageType.Cellebrite,
        path: null,
        baseDir: "",
        files: {
          active: false,
          selectedIndex: -1,
          list: []
        },
        getListings: () => {
          sftpService.GetUploadsListing(scope.project, scope.source.baseDir, scope.source.forensicImageType).then(response => {
            scope.source.files.type = response.data.type;
            scope.source.files.active = false;
            scope.source.files.list = response.data.files;
          }, () => {
            scope.source.files = savedFilesState;
            notificationService.addError({
              title: 'Error fetching from SFTP',
              description: "That's embarrassing, please try again later",
              timeout: 5000
            });
          });
        }
      },
    };

    scope.SourceType = SourceType;
    scope.ForensicImageType = ForensicImageType;
    scope.Status = Status;
    scope.PIIDetectionMode = PIIDetectionMode;
    scope.newCollection = new Collection(
      scope.project.id,
      "",
      "",
      "",
      "",
      "",
      "",
      true,
      true,
      false,
      false,
      false,
      false,
      false,
      false,
      false,
      false);
    scope.project = project;
    scope.saving = false;
    scope.validating = false;
    scope.uploading = false;
    scope.verifying = false;
    scope.step = 1;
    scope.maxSteps = 2;
    scope.source = {...sources.upload};
    scope.acceptTermsAndConditions = scope.project.status === Status.Trial;
    scope.isOrganizationProject = !!project.organizationId;
    scope.username = accountService.GetEmail();

    custodianService.FindAll(project.id).then(custodians => scope.custodians = custodians);

    scope.$watch("source.imap.ssl", val => (val && (scope.source.port = 993)) || (scope.source.port = 143));

    scope.$watch("source.ftp.secure", val => val && (scope.source.type = SourceType.SFTP));

    scope.$watch("source.type", (val: SourceType, oldVal: SourceType) => {
      if (val) {
        if (oldVal === SourceType.Upload && val !== SourceType.Upload) {
          savedUploadSource = { files: scope.source.files, valid: scope.source.valid || "" };
        }
        switch (val) {
          case SourceType.Upload:
            scope.source = {
              ...sources.upload,
              files: savedUploadSource.files,
              valid: savedUploadSource.valid,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.IIO:
            scope.source = {
              ...sources.iio,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            scope.refresh();
            break;
          case SourceType.IIO_Qumulo:
            scope.source = {
              ...sources.iio_qumulo,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            scope.refresh();
            break;
          case SourceType.FTP:
            scope.source = {
              ...sources.ftp,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.S3:
            scope.source = {
              ...sources.s3,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.S3_Qumulo:
            scope.source = {
              ...sources.s3_qumulo,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              endpoint: scope.source.endpoint,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.IMAP:
            scope.source = {
              ...sources.imap,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.DropBox:
            scope.source = {
              ...sources.dropbox,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            break;
          case SourceType.ForensicImage:
            scope.source = {
              ...sources.forensicImage,
              user: scope.source.user,
              accessToken: scope.source.accessToken,
              bucketName: scope.source.bucketName,
              accessKey: scope.source.accessKey,
              secretKey: scope.source.secretKey
            };
            scope.refresh();
            break;
        }

        Object.keys(scope.source).forEach(key => scope.source[key] === undefined && delete scope.source[key])
      }
    });

    scope.$on("fileDropped", (e, file) => {
      scope.source.valid = "valid";
      scope.source.files.file = file;
    });

    scope.$on("droppedFileRemoved", () => {
      scope.source.valid = "";
      scope.source.files.file = null;
    });

    scope.$watch("newCollection.custodian", val => {
      if (_.isObject(val)) {
        scope.newCollection.custodian = val;
      } else if (_.isString(val)) {
        scope.newCollection.custodian = {name: val};
      }
    });

    scope.$watch("source.ftp.secure", value => scope.source.port = value ? 22 : 21);

    scope.close = () => {
      if (scope.saving) return;
      $uibModalInstance.close();
    };

    scope.save = collection => {
      if (scope.saving) {
        console.log('Collection already saved, skipping...');
        return;
      }

      if (scope.source.type === SourceType.ForensicImage && !scope.validating) {
        $timeout(function () {
          notificationService.addSuccess({
            title: `Image validating`,
            description: `${scope.source.forensicImageType} image is being validated, you will be notified when processing begins.`,
            timeout: 5000
          });
        }, 2000);

        scope.validating = true;
        $timeout(() => $uibModalInstance.close(), 2000);

        processingService.ValidateForensicImage(scope.project.id, scope.source, new Collection(
          collection.projectId,
          collection.custodian.name,
          collection.name,
          collection.description,
          collection.ritm,
          collection.evidence,
          collection.release,
          collection.extractFromContainers,
          collection.extractAttachmentAndEmbeddedDocumentsFromEmails,
          collection.extractImagesFromEmails,
          collection.extractDocumentsFromFiles,
          collection.extractImagesFromFiles,
          collection.imagePresentationsWithNotes,
          collection.cancelTranscription,
          collection.cancelExtractEntities,
          collection.cancelPdf,
          collection.cancelOcr,
          collection.piiDetectionMode
        ));


        rootScope.$on("FeedService.ForensicImageValidationComplete", (event, message) => {
          if (message.status === ForensicImageValidationStatus.VALID) {
            notificationService.addSuccess({
              title: 'Forensic Image is valid, processing started.',
              description: `The forensic image ${scope.source.path} is a valid ${scope.source.forensicImageType} image format, processing started.`,
              timeout: 5000
            });

            $location.path('/project/' + collection.projectId + "/collection/" + message.collectionId);
          } else {
            scope.validating = false;
            notificationService.addError({
              title: 'Forensic Image is invalid.',
              description: `The forensic image ${scope.source.path} does not conform to the ${scope.source.forensicImageType} image format.`,
              timeout: 5000
            });
          }
        });

        return;
      }

      scope.validating = false;
      scope.saving = true;
      console.log('saving');
      console.log(collection);

      const newCollection = collectionService.Create(new Collection(
        collection.projectId,
        collection.custodian.name,
        collection.name,
        collection.description,
        collection.ritm,
        collection.evidence,
        collection.release,
        collection.extractFromContainers,
        collection.extractAttachmentAndEmbeddedDocumentsFromEmails,
        collection.extractImagesFromEmails,
        collection.extractDocumentsFromFiles,
        collection.extractImagesFromFiles,
        collection.imagePresentationsWithNotes,
        collection.cancelTranscription,
        collection.cancelExtractEntities,
        collection.cancelPdf,
        collection.cancelOcr,
        collection.piiDetectionMode
      ));

      let processing = false;

      collectionService.Save(newCollection).then((collection: Collection) => {
          let file = null;

          if (processing) {
            console.log('Collection already processing, skipping...');
            return;
          }

          console.log('Collection saved, uploading');

          if (scope.source.files && scope.source.files.file) {
            scope.saving = false;
            scope.source.files.active = true;
            file = scope.source.files.file;
          }

          processingService.ProcessData(collection, scope.source, file,
            (loaded, total, percent) => {
              $timeout(() => scope.$apply(() => {
                scope.uploadBytesLoaded = loaded;
                scope.uploadBytesTotal = total;
                scope.uploadProgress = percent;
              }))
            },
            () => {
              console.log('SUCCESS! ' + collection);
              $timeout(() =>
                scope.$apply(() => {
                  scope.saving = false;
                  scope.uploading = false;
                  processing = false;
                  scope.close();
                  notificationService.addSuccess({
                    title: 'Processing started',
                    description: '',
                    timeout: 5000
                  });

                  $location.path('/project/' + collection.projectId + "/collection/" + collection.id);
                }));
            },
            () => {
              $timeout(() =>
                scope.$apply(() => {
                  scope.saving = false;
                  scope.uploading = false;
                  processing = false;
                  scope.close();

                  notificationService.addError({
                    title: 'Error starting processing',
                    description: "That's embarrassing, please try again later",
                    timeout: 5000
                  });
                }));
            });
        },
        () => {
          // error
          scope.saving = false;
          processing = false;
        });
    };

    // When clicked
    scope.initialize = project => {
      scope.newCollection.projectId = project.id;
      scope.step = 1;
    };

    scope.back = () => scope.step--;

    scope.next = () => scope.step++;

    /**********************************************************
     * IIO, AccessID Qumulo, FTP, S3
     **********************************************************/

    scope.refreshUp = () => {
      let path = scope.source.baseDir;

      if (path) {
        const parts = path.split('/').filter(Boolean);
        parts.pop();
        path = parts.join("/");
      }

      scope.refresh(path, true);
    };

    scope.refresh = (baseDir, isDirectory = false) => {
      if (!isDirectory && baseDir) {
        return;
      }

      // Save the current files state before modifying it, so errors can restore it
      savedFilesState = {
        type: scope.source.files.type,
        active: scope.source.files.active,
        selectedIndex: scope.source.files.selectedIndex,
        list: scope.source.files.list
      };

      scope.source.valid = "";
      scope.source.files.active = scope.source.type !== SourceType.Upload;
      scope.source.files.selectedIndex = -1;
      scope.source.files.list = [];
      scope.source.files.type = "";

      scope.source.baseDir = baseDir || "";

      if (scope.source.getListings) {
        scope.source.getListings();
      }
    };

    scope.selectFile = ($index, file) => {
      scope.source.valid = "valid";
      scope.source.files.selectedIndex = $index;
      scope.source.path = file.path || file.relativePath;
    };

    scope.processIMAP = () => {
      if (scope.project.status === Status.Trial) {
        $uibModal.open({
          backdrop: "static",
          keyboard: true,
          backdropClick: false,
          templateUrl: "partials/dialog_credit_card.html",
          controller: "CreditCardModalController",
          scope: scope,
          resolve: {
            project: () => scope.project,
            message: () => "Activate account to process your IMAP collection"
          }
        });
      } else {
        scope.verifying = true;
        processingService.VerifyIMAPCredentials(scope.newCollection, scope.source, () => {
          scope.verifying = false;
          scope.save(scope.newCollection);
        }, error => {
          $timeout(() => {
            scope.$apply(() => {
              scope.verifying = false;

              notificationService.addError({
                title: 'Error connecting to IMAP service',
                description: error.responseText
              });
            });
          });
        });
      }
    };

    /**********************************************************
     * Dropbox
     **********************************************************/

    scope.refreshDropbox = baseDir => {
      scope.source.baseDir = baseDir || "/";
      scope.source.valid = "";
      scope.source.files.active = true;
      scope.source.files.selectedIndex = -1;
      scope.source.files.list = [];
      scope.source.baseDir = baseDir || "/";

        if (scope.source.accessToken === undefined) {
          dropboxOAuthService.getAuthorizationUrl().then(response => {
            const handle = window.open(response);

            if (!handle || handle.closed || typeof handle.closed == 'undefined') {
              notificationService.addError({
                title: "Popup Blocker",
                description: "Please disable popup blocker",
                timeout: 5000
              });
              scope.source.files.active = false;
            } else {
              const onMessage = async (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                const { code, state } = event.data;
                if (!code) return;

                window.removeEventListener('message', onMessage);

                const authenticationInfo = await dropboxOAuthService.getAuthorizationToken(state, code, "");
                scope.source.accessToken = authenticationInfo.accessToken;
                scope.source.user = authenticationInfo.userId;

                scope.$applyAsync();
              };

              window.addEventListener('message', onMessage);
            }
          });
        } else {
          storageService.GetDropboxListing(scope.source).success(response => {
            scope.source.files.active = false;
            scope.source.files.list = response.files;
          }).error(() => scope.source.files.active = false);
        }
      };

    scope.$watch("source.user", value => {
      if (!value || value === '') {
        return;
      }

      storageService.GetDropboxListing(scope.source).success(response => {
        scope.source.files.active = false;
        scope.source.files.list = response.files;
      }).error(() => scope.source.files.active = false);
    }, true);

    scope.logoutDropbox = () => {
      const logoutWindow = window.open("https://www.dropbox.com/logout");

      $timeout(() => {
        logoutWindow.close();
        scope.source.path = "";
        scope.source.baseDir = "/";
        scope.source.user = "";
        scope.source.accessToken = "";
        scope.source.valid = "";
        scope.source.files.active = false;
        scope.source.files.selectedIndex = -1;
        scope.source.files.list = [];
      }, 100);
    };

    scope.refresh();
  }
}

frontendApp.controller("AddCollectionController", ["$rootScope", "$scope", "$timeout", "$location", "$uibModalInstance", "$uibModal", "project", "CollectionService", "ProcessingService", "NotificationService", "SFTPService", "StorageService", "DropboxOAuthService", "CustodianService", "AccountService", Factory.Create(AddCollectionController)]);
